import { Role } from '@policyfly/protobuf/pfbingo'
import {
  DisplayIf,
  Permissions,
  SchemaSettings_Endpoints_Cancellation,
  SchemaSettings_Endpoints_EndorsementQuote,
  SchemaSettings_Endpoints_Issuance,
  SchemaSettings_ProgramRating,
  RoundingMode, Formatter,
  InputData_Protobuf_Bucket,
  DateMode,
  SchemaSettings_DefaultVisibility_Visibility,
} from '@policyfly/protobuf/pfschema'
import { pathToApiPath } from '@policyfly/utils/api'
import { defineStore } from 'pinia'

import { useApiStore } from '@/stores/api'
import { useAuthenticationStore } from '@/stores/authentication'

import { useApplicationListStore } from './applicationList'
import { useProtobufStore } from './protobuf'
import { loadTranslations } from '@/plugins/i18n'
import { hotReloadStore } from '@/utils/build'
import { basicFormatter } from '@/utils/formatter'
import { getInputData, getInputDataPath } from '@/utils/inputData'

import type { SchemaSettings } from '@policyfly/protobuf/pfschema'

export interface State extends Required<SchemaSettings> {
  slug: string | null
}

export const useSettingsStore = defineStore('settings', {
  state: (): State => ({
    slug: null,
    statuses: {
      application: [],
      endorsement: [],
      renewal: [],
      cancellation: [],
      extension: [],
    },
    canAuthoriseIf: DisplayIf.create(),
    authorisationDialog: [],
    authorisationCheckbox: [],
    effectiveDatePath: 'effectiveDate',
    additionalAppFilterStatuses: [],
    roles: [{
      role: Role.ROLE_PROGRAM_ADMIN,
      display: 'Program Admin',
      permissions: Permissions.create(),
    }],
    reportingPage: true,
    decimals: true,
    roundingMode: RoundingMode.ROUNDING_MODE_DOWN,
    dateMode: DateMode.DATE_MODE_LOCALE,
    validateInitialApp: true,
    validateAppOnLoad: false,
    restrictArchive: true,
    applicationReviewer: 'a Program Administrator',
    fab: {
      customItems: [],
    },
    feedEvent: {},
    multiQuoteText: 'ADD ANOTHER QUOTE',
    forceCancellationNegatives: false,
    allowMultiQuoteOnEndorsement: false,
    rating: SchemaSettings_ProgramRating.PROGRAM_RATING_NONE,
    documentManagement: false,
    endorsementCorrectionReport: false,
    cancellationRequests: { type: { oneofKind: undefined } },
    formAI: false,
    formAIDocumentName: 'Application',
    renewsToNewPolicy: true,
    progminOnlySignatureApproval: false,
    signableDocuments: {
      approvalMessage: '',
      documents: [],
    },
    coverages: [],
    endpoints: {
      issuance: SchemaSettings_Endpoints_Issuance.ISSUANCE_TO_ISSUED,
      cancellation: SchemaSettings_Endpoints_Cancellation.CANCELLATION_QUOTE,
      endorsementQuote: SchemaSettings_Endpoints_EndorsementQuote.ENDORSEMENT_QUOTE_QUOTE_REDIRECT,
    },
    hasReinstatementWorkflow: false,
    skipAppTwoIf: DisplayIf.create(),
    issueSubmitText: 'SUBMIT',
    quoteSubmitText: {
      value: {
        oneofKind: 'string',
        string: 'SUBMIT',
      },
    },
    protobuf: false,
    banners: {
      userTips: [],
      alerts: [],
    },
    homeDisplay: {
      coverages: false,
      chips: false,
      tiv: false,
      effective: true,
      status: true,
      assignee: true,
      ownedBy: true,
      modified: true,
      notifications: true,
    },
    programData: {
      fields: {},
    },
    requiredIndicators: false,
    showCoverageIndicators: false,
    facilityMarkets: [],
    carriers: [],
    defaultVisibility: {
      attachment: SchemaSettings_DefaultVisibility_Visibility.VISIBILITY_INTERNAL,
      comment: SchemaSettings_DefaultVisibility_Visibility.VISIBILITY_INTERNAL,
      task: SchemaSettings_DefaultVisibility_Visibility.VISIBILITY_GLOBAL,
    },
  }),

  getters: {
    activeCoverages (): State['coverages'] {
      const coverages = this.coverages || []
      return coverages.filter((c) => !c.displayOnly && getInputData(c.data!, { defaultBucket: InputData_Protobuf_Bucket.BUCKET_PS }))
    },
    hasCancellationRequests (): boolean {
      return !!this.cancellationRequests.type.oneofKind
    },
    /**
     * Returns coverages for the loaded program with apiPaths that match the backend enum strings,
     * for use in api requests and schedule service item coverage associations.
     */
    coveragesApiPaths (): (State['coverages'][number] & { apiPath: string, active: boolean })[] {
      return this.coverages.map((coverage) => {
        return {
          ...coverage,
          active: !!coverage.data && !!getInputData(coverage.data, { defaultBucket: InputData_Protobuf_Bucket.BUCKET_PS }) && !coverage.displayOnly,
          apiPath: coverage.data ? pathToApiPath(getInputDataPath(coverage.data)) : '',
        }
      })
    },
    /**
     * The formatter to use for displayed currency values
     * Will use the program currency code if defined
     */
    currencyFormatter (): Formatter {
      const protobufStore = useProtobufStore()
      return Formatter.create({
        name: {
          oneofKind: 'customCurrency',
          customCurrency: {
            decimalPoints: { value: { oneofKind: 'number', number: 2 } },
            roundingMode: { value: { oneofKind: 'program', program: true } },
            ...this.protobuf && protobufStore.workingCopies?.policyState?.currencyInfo
              ? { prefix: 'base' }
              : { symbol: '$' },
          },
        },
      })
    },
    /**
     * The formatter to use for displayed date values.
     * For a month only date, use `monthDateFormatter` instead.
     */
    dateFormatter (): Formatter {
      return this.dateMode === DateMode.DATE_MODE_FRIENDLY
        ? basicFormatter('friendlyDate')
        : basicFormatter('date')
    },
    /**
     * The formatter to use for displayed month only date values.
     */
    monthDateFormatter (): Formatter {
      return this.dateMode === DateMode.DATE_MODE_FRIENDLY
        ? { name: { oneofKind: 'customDate', customDate: { options: { month: 'long', year: 'numeric' } } } }
        : { name: { oneofKind: 'customDate', customDate: { options: { month: '2-digit', year: 'numeric' } } } }
    },
  },

  actions: {
    async load (): Promise<void> {
      const authenticationStore = useAuthenticationStore()
      const applicationListStore = useApplicationListStore()
      const apiStore = useApiStore()
      const slug = authenticationStore.slug
      if (slug === this.slug) return
      const config = await apiStore.schema.settings({ slug })
      const translations = await apiStore.schema.translations({ slug })
      this.$reset()
      const settingsAssign: Partial<State> = {
        ...Object.fromEntries(
          // remove `undefined` values to allow store defaults to take priority
          Object.entries(config).filter(([, value]) => value !== undefined),
        ),
        slug,
      }
      Object.assign(this, settingsAssign)
      loadTranslations(slug, translations)
      applicationListStore.resetHeaders()
    },
  },
})

hotReloadStore(useSettingsStore)
